import React from "react";
import { OptimizedGantt } from "@planzone/dto";
import { DateTime } from "luxon";
import { Context } from "@scenes/optimizedGantt/context";
import { reCalculateDateRange } from "@scenes/optimizedGantt/utils";
import { useElementContext } from "../context";
import { getDate } from "../utils/getDate";
import { isDone, isEvent, isMilestone, isTask } from "../utils";
import { Props } from "./types";
import styles from "./styles.scss";

function getEndDate(
  element: OptimizedGantt[number],
  recalculatedEndDate: DateTime,
  userTimezone: string,
): DateTime {
  const completionDate = getDate(element.completionDate, userTimezone);

  return isDone(element) &&
    !!element.endDate &&
    !!element.startDate &&
    isTask(element)
    ? completionDate
    : getDate(
        element.endDate || (recalculatedEndDate.toUTC().toISO() ?? ""),
        userTimezone,
      );
}

function getOvertime(
  element: OptimizedGantt[number],
  limitDate: DateTime,
  recalculatedEndDate: DateTime,
  userTimezone: string,
): number {
  // Bugfix preserved from Phase 1 commit `5a6a1ac42`: previously the
  // `limitDate.toString()` round-trip ran through a locale-dependent string
  // format. Now operates directly on Luxon DateTime values throughout.
  const end = getEndDate(element, recalculatedEndDate, userTimezone);
  const today = DateTime.now().setZone(userTimezone).startOf("day");
  const lDate = limitDate.startOf("day");
  const adjustedEnd = end > lDate ? lDate : end;
  return today.diff(adjustedEnd, "days").days;
}

function Overtime({ element, isDisabled, limitDate, children }: Props) {
  const userTimezone = DateTime.local().zoneName;
  const { cell, areDelaysToggled } = React.useContext(Context);
  const { state } = useElementContext();
  const recalculated = reCalculateDateRange(
    element,
    DateTime.fromISO(state.minDate, { zone: "utc" }),
    DateTime.fromISO(state.maxDate, { zone: "utc" }),
  );
  const overtime = getOvertime(
    element,
    limitDate,
    recalculated.endDate,
    userTimezone,
  );
  const canPrintOvertime =
    !isMilestone(element) && !isEvent(element) && !isDone(element);
  const hasOvertime = overtime > 0;

  return (
    <div className={styles.overtime}>
      {children(overtime)}
      {canPrintOvertime && hasOvertime && (
        <div
          className={styles.indicator}
          style={{
            width: isDisabled || !areDelaysToggled ? 0 : cell.width * overtime,
          }}
        />
      )}
    </div>
  );
}

export { Overtime };
